import { Shield, ArrowLeft, Mail, Phone, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CRISIS_RESOURCES } from "@/lib/crisisResources";

// Privacy Notice Version - update this when content changes
export const PRIVACY_NOTICE_VERSION = "1.0";
export const PRIVACY_NOTICE_DATE = "30/08/2026";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f0e8] to-white">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="ghost" className="mb-4 text-[#5a8a5a] hover:bg-[#5a8a5a]/10">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-[#5a8a5a]" />
            <h1 className="text-3xl font-heading font-bold text-[#1a2e1a]">PRIVACY NOTICE</h1>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-slate max-w-none space-y-6 text-[#1a2e1a]/80">
          <p className="text-lg font-medium">
            Compass by TrueNorth Zone<br />
            Version {PRIVACY_NOTICE_VERSION}
          </p>

          <p>
            ZenZone (Pty) Ltd, registration number 2023/782263/07, trading as
            TrueNorth Zone ("we") operates Compass. This notice explains what we
            collect, where it goes, and what you can do about it. It is written to
            meet the Protection of Personal Information Act, 2013 (POPIA).
          </p>

          <h2 className="text-xl font-semibold text-[#1a2e1a] mt-8">WHO IS RESPONSIBLE</h2>
          <p>
            ZenZone (Pty) Ltd, registration number 2023/782263/07, trading as
            TrueNorth Zone.<br />
            Contact: <a href="mailto:info@truenorth-zone.com" className="text-[#5a8a5a] hover:underline">info@truenorth-zone.com</a>
          </p>

          <h2 className="text-xl font-semibold text-[#1a2e1a] mt-8">WHAT WE COLLECT</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Your name and email address, when you register.</li>
            <li>What you write: journal entries, gratitude entries, check-in notes, and messages you send to PathFinder.</li>
            <li>What you record: mood and energy scores, daily check-ins, streaks, quest and course progress.</li>
            <li>Payment information, if you subscribe. Card details are handled by PayFast and never reach us.</li>
          </ul>

          <h2 className="text-xl font-semibold text-[#1a2e1a] mt-8">WHY WE COLLECT IT</h2>
          <p>
            To run the app: to show you your own history and patterns, to generate
            PathFinder responses and weekly reflections, to send you reminders if
            you ask for them, and to process payment if you subscribe. We do not
            sell your information, and we do not use it for advertising.
          </p>

          <h2 className="text-xl font-semibold text-[#1a2e1a] mt-8">WHERE YOUR INFORMATION GOES</h2>
          <p>
            Compass runs on Emergent, a hosting platform. Your data, including
            everything you write, is stored and processed on Emergent's
            infrastructure in the United States and India. It therefore leaves South
            Africa.
          </p>
          <p>
            PathFinder and the weekly reflections are generated by Anthropic's
            Claude. When you use PathFinder, the following is sent to Anthropic
            through Emergent: your message, recent journal excerpts, your mood and
            energy scores, your streak, and your recent PathFinder messages. This
            content is subject to Emergent's and Anthropic's terms. We are not able
            to guarantee how those parties retain or use it beyond what their
            published terms state.
          </p>
          <p>
            Emails are sent through Resend, which receives your name and email
            address. Payments are processed by PayFast, which receives your name,
            email address and payment amount.
          </p>
          <p>
            By registering, you consent to your personal information being
            transferred to and processed in these countries, as required by section
            72 of POPIA.
          </p>

          <h2 className="text-xl font-semibold text-[#1a2e1a] mt-8">HOW LONG WE KEEP IT</h2>
          <p>
            For as long as your account exists.<br />
            If you delete your account, your data is removed from the live database
            immediately. Copies persist in Emergent's automated backups and expire
            within twelve months. We cannot delete data from those backups before
            they expire.
          </p>

          <h2 className="text-xl font-semibold text-[#1a2e1a] mt-8">SECURITY</h2>
          <p>
            Passwords are hashed and never stored in readable form. Connections are
            encrypted. Emergent encrypts data at rest and restricts staff access.
            No system is perfectly secure, and we do not claim otherwise.
          </p>

          <h2 className="text-xl font-semibold text-[#1a2e1a] mt-8">YOUR RIGHTS UNDER POPIA</h2>
          <p>You may:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>see what we hold about you. Use Export My Data in your profile to download everything, at any time, without asking us.</li>
            <li>correct anything inaccurate.</li>
            <li>delete your account and all its data. Use Delete My Account in your profile.</li>
            <li>withdraw this consent, by deleting your account.</li>
            <li>object to how we process your information.</li>
            <li>complain to the Information Regulator of South Africa: <a href="mailto:inforeg@justice.gov.za" className="text-[#5a8a5a] hover:underline">inforeg@justice.gov.za</a></li>
          </ul>
          <p>
            To exercise any of these, contact <a href="mailto:info@truenorth-zone.com" className="text-[#5a8a5a] hover:underline">info@truenorth-zone.com</a>.
          </p>

          <h2 className="text-xl font-semibold text-[#1a2e1a] mt-8">WHAT COMPASS IS NOT</h2>
          <p>
            Compass is a self-development tool. It is not therapy, medical care, or
            a substitute for professional help. Nothing in it is clinical advice. If
            you are struggling, please speak to a qualified professional. In South
            Africa, the SADAG Suicide Crisis Helpline is {CRISIS_RESOURCES.phone}, available 24
            hours, or SMS {CRISIS_RESOURCES.sms} for a call-back. For a referral to a psychologist or
            psychiatrist, the SADAG Mental Health Line is {CRISIS_RESOURCES.mentalHealthLine}, open 8am to
            8pm daily.
          </p>

          <h2 className="text-xl font-semibold text-[#1a2e1a] mt-8">CHANGES</h2>
          <p>
            If this notice changes materially, we will ask you to accept the new
            version.<br />
            Version {PRIVACY_NOTICE_VERSION}, {PRIVACY_NOTICE_DATE}
          </p>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-[#a69373]/20 text-center text-sm text-[#a69373]/60">
          <p>© {new Date().getFullYear()} TrueNorth Zone. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
